import {
  Dialog,
  DialogContent,
  Box,
  IconButton,
  Typography,
  Button,
  Chip,
} from "@mui/material";
import { Close, Download, Print, Delete } from "@mui/icons-material";
import { ImageFile } from "./request";

type Labels = {
  sizeLabel: string;
  modifiedLabel: string;
  scanDownload: string;
  qrAlt: string;
  download: string;
  print: string;
  delete: string;
  deleting: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  image: ImageFile | null;
  isMobile: boolean;
  labels: Labels;
  deleting?: boolean;
  scoreLabel?: string;
  onDownload: () => void;
  onPrint: () => void;
  onDelete?: () => void;
  getQRCodeUrl: (url: string) => string;
};

function applyFaceOverlay(img: HTMLImageElement, bbox: string | undefined) {
  const overlay = img.parentElement?.querySelector(".face-overlay") as HTMLElement | null;
  if (!overlay) return;
  const parts = bbox ? bbox.split(",").map(Number) : null;
  const hasBbox = parts && parts.length === 4 && parts.some((v) => v > 0);
  if (!hasBbox || !parts) {
    overlay.style.display = "none";
    return;
  }
  const natW = img.naturalWidth;
  const natH = img.naturalHeight;
  if (natW === 0 || natH === 0) return;
  const [left, top, right, bottom] = parts;
  const faceW = right - left;
  const faceH = bottom - top;
  const padX = faceW * 0.2;
  const padY = faceH * 0.2;
  const pctL = Math.max(0, ((left - padX) / natW) * 100);
  const pctT = Math.max(0, ((top - padY) / natH) * 100);
  const pctW = Math.min(100 - pctL, ((faceW + 2 * padX) / natW) * 100);
  const pctH = Math.min(100 - pctT, ((faceH + 2 * padY) / natH) * 100);
  overlay.style.left = pctL + "%";
  overlay.style.top = pctT + "%";
  overlay.style.width = pctW + "%";
  overlay.style.height = pctH + "%";
  overlay.style.display = "block";
}

export default function ImageDetailDialog({
  open,
  onClose,
  image,
  isMobile,
  labels,
  deleting,
  scoreLabel,
  onDownload,
  onPrint,
  onDelete,
  getQRCodeUrl,
}: Props) {
  if (!image) return null;
  const hasBbox = Boolean(image.faceBbox);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isMobile ? false : "lg"}
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 4 } }}
    >
      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          minHeight: isMobile ? "100vh" : "500px",
          height: isMobile ? "100vh" : "auto",
        }}
      >
        <Box
          sx={{
            flex: isMobile ? "1 1 auto" : 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: isMobile ? "#101114" : "#f5f5f5",
            minHeight: 0,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box sx={{ position: "relative", display: "inline-block", maxWidth: "100%", maxHeight: "100%" }}>
            <img
              key={(image.url || "") + (image.faceBbox || "")}
              src={image.url || image.thumbnail_url || ""}
              alt={image.name}
              style={{
                maxWidth: "100%",
                maxHeight: isMobile ? "100%" : "80vh",
                objectFit: "contain",
                width: isMobile ? "100%" : "auto",
                display: "block",
              }}
              onLoad={(e) => applyFaceOverlay(e.currentTarget, image.faceBbox)}
            />
            {hasBbox && (
              <Box
                className="face-overlay"
                sx={{
                  display: "none",
                  position: "absolute",
                  outline: "2px solid rgba(244,67,54,0.85)",
                  outlineOffset: "-1px",
                  borderRadius: "2px",
                  pointerEvents: "none",
                  boxShadow: "0 0 0 2px rgba(244,67,54,0.25), 0 0 10px 2px rgba(244,67,54,0.15)",
                }}
              />
            )}
          </Box>
          {isMobile && (
            <IconButton
              onClick={onClose}
              sx={{
                position: "absolute",
                top: "calc(8px + env(safe-area-inset-top))",
                right: 8,
                bgcolor: "rgba(255,255,255,0.12)",
                color: "white",
                backdropFilter: "blur(4px)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
              }}
            >
              <Close />
            </IconButton>
          )}
        </Box>
        <Box
          sx={{
            flex: isMobile ? "0 0 auto" : 1,
            p: isMobile ? 2 : 3,
            minWidth: isMobile ? "auto" : "300px",
            display: "flex",
            flexDirection: "column",
            maxHeight: isMobile ? "46vh" : "none",
            overflow: isMobile ? "auto" : "visible",
            bgcolor: "background.paper",
            borderRadius: isMobile ? "16px 16px 0 0" : 0,
            boxShadow: isMobile ? "0 -4px 20px rgba(0,0,0,0.25)" : "none",
            position: "relative",
          }}
        >
          {!isMobile && (
            <IconButton onClick={onClose} sx={{ position: "absolute", top: 8, right: 8 }}>
              <Close />
            </IconButton>
          )}

          <Typography
            variant="h6"
            gutterBottom
            sx={{ mt: isMobile ? 0 : 2, wordBreak: "break-all", fontSize: isMobile ? 16 : 20 }}
          >
            {image.name}
          </Typography>

          {(image.size_formatted || image.size) && (
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {labels.sizeLabel}
              {image.size_formatted || image.size}
            </Typography>
          )}

          {image.mtime && (
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {labels.modifiedLabel}
              {image.mtime}
            </Typography>
          )}

          {scoreLabel && <Chip label={scoreLabel} color="error" size="small" sx={{ mt: 0.5, alignSelf: "flex-start" }} />}

          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: isMobile ? "flex-start" : "center",
              alignItems: "center",
              mt: isMobile ? 0.5 : 3,
            }}
          >
            <Typography variant={isMobile ? "body2" : "subtitle1"} color="text.secondary" gutterBottom>
              {labels.scanDownload}
            </Typography>

            <img
              src={getQRCodeUrl(image.url)}
              alt={labels.qrAlt}
              style={{
                width: isMobile ? 120 : 200,
                height: isMobile ? 120 : 200,
                border: "1px solid #e0e0e0",
                borderRadius: 8,
              }}
            />

            <Box
              sx={{
                mt: isMobile ? 1.5 : 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                width: "100%",
                pb: isMobile ? "env(safe-area-inset-bottom)" : 0,
              }}
            >
              <Button variant="contained" startIcon={<Download />} onClick={onDownload} fullWidth disableElevation>
                {labels.download}
              </Button>

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexDirection: isMobile ? "row" : "column",
                }}
              >
                <Button variant="outlined" startIcon={<Print />} onClick={onPrint} fullWidth>
                  {labels.print}
                </Button>
                {onDelete && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={onDelete}
                    disabled={deleting}
                    fullWidth
                  >
                    {deleting ? labels.deleting : labels.delete}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
